import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    InputNumber,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

const { TextArea } = Input;
const { Option } = Select;



interface GSTModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        gst_id?: number;
        gst_name?: string;
        gst_cgst?: number;
    } | null;
    isEditMode?: boolean;
}

const GSTModal: React.FC<GSTModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddSc = async (values: any) => {
        try {
            const dataVal = {
                gst_name: values.gst_name,
                gst_cgst: values.gst_cgst,
                gst_sgst: values.gst_sgst,
                gst_igst: values.gst_igst,
                ...(initialValues?.gst_id && { gst_id: initialValues.gst_id }),
            };
            const url = isEditMode
                ? 'edit-gst'
                : 'add-gst';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'GST updated successfully!' : 'GST created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} GST`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                gst_name: initialValues.gst_name,
                gst_cgst: initialValues.gst_cgst,
                gst_sgst: initialValues.gst_cgst,
                gst_igst: initialValues.gst_cgst,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit GST' : 'Add New GST'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddSc}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="gst_name"
                            label={
                                <span>
                                    GST Percentage&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 255 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the GST Name' },
                                { max: 255, message: 'Maximum 255 characters allowed' },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={255}
                                placeholder="e.g., GST "

                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="gst_cgst"
                            label={
                                <span>
                                    CGST &nbsp;
                                </span>
                            }
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.reject('CGST is required');
                                        }
                                        if (typeof value !== 'number' || isNaN(value)) {
                                            return Promise.reject('CGST must be a valid number');
                                        }
                                        if (value < 0 || value > 14) {
                                            return Promise.reject('CGST must be between 0.00 and 14.00');
                                        }
                                        const decimalPart = value.toString().split('.')[1];
                                        if (decimalPart && decimalPart.length > 2) {
                                            return Promise.reject('CGST can have at most 2 decimal places');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="e.g., 15.50"
                                min={0}
                                max={999.99}
                                step={0.01}
                                precision={2}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="gst_sgst"
                            label={<span>SGST &nbsp;</span>}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.reject('SGST is required');
                                        }
                                        if (typeof value !== 'number' || isNaN(value)) {
                                            return Promise.reject('SGST must be a valid number');
                                        }
                                        if (value < 0 || value > 14) {
                                            return Promise.reject('SGST must be between 0.00 and 14.00');
                                        }
                                        const decimalPart = value.toString().split('.')[1];
                                        if (decimalPart && decimalPart.length > 2) {
                                            return Promise.reject('SGST can have at most 2 decimal places');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="e.g., 7.50"
                                min={0}
                                max={14}
                                step={0.01}
                                precision={2}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="gst_igst"
                            label={<span>IGST &nbsp;</span>}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        // Allow empty input (null/undefined/empty string)
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.resolve();
                                        }
                                        if (typeof value !== 'number' || isNaN(value)) {
                                            return Promise.reject('IGST must be a valid number');
                                        }
                                        if (value < 0 || value > 28) {
                                            return Promise.reject('IGST must be between 0.00 and 28.00');
                                        }
                                        const decimalPart = value.toString().split('.')[1];
                                        if (decimalPart && decimalPart.length > 2) {
                                            return Promise.reject('IGST can have at most 2 decimal places');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            normalize={(value) => {
                                // If user clears the input, set to 0
                                if (value === undefined || value === null || value === '') {
                                    return 0;
                                }
                                return value;
                            }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="e.g., 12.00"
                                min={0}
                                max={28}
                                step={0.01}
                                precision={2}
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New GST
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default GSTModal;